import React from 'react';
import type { ComponentProps } from 'react';
import styled from '@emotion/styled';
import type { AlignItemsProps, AlignSelfProps, DisplayProps, FlexWrapProps } from 'styled-system';
import { alignItems, alignSelf, display, flexWrap, space, themeGet } from 'styled-system';

import type { SpaceProps } from '../../theme/system-types';
import {
  BodyRegularBR2,
  BodyStrikeST2,
  CaptionStrikeCS1,
  HeadlineH3,
  HeadlineH4,
  HeadlineH5,
} from '../Typography';

const enum TextColor {
  Default = 'colors.sys.neutral.text.active',
  Sale = 'colors.sys.primary.text.default',
  SaleV2 = 'colors.sys.marketing.orange',
}

const UnstyledPriceContainer = styled(HeadlineH4)<AlignItemsProps & FlexWrapProps & DisplayProps>`
  ${alignItems};
  ${flexWrap};
  ${display};
`;

const PriceContainer = (props: ComponentProps<typeof UnstyledPriceContainer>) => (
  <UnstyledPriceContainer alignItems="flex-end" flexWrap="wrap" display="flex" {...props} />
);

const UnstyledPriceContainerLarge = styled(BodyRegularBR2)<
  AlignItemsProps & FlexWrapProps & DisplayProps
>`
  ${alignItems};
  ${flexWrap};
  ${display};
`;

const PriceContainerLarge = (props: ComponentProps<typeof UnstyledPriceContainerLarge>) => (
  <UnstyledPriceContainerLarge alignItems="flex-end" flexWrap="wrap" display="flex" {...props} />
);

type PriceProps = DisplayProps &
  AlignSelfProps &
  SpaceProps & {
    isOnSale: boolean;
    withCompactStyle?: boolean;
  };

const UnstyledPrice = styled(HeadlineH5)<PriceProps>`
  ${display};
  ${alignSelf};
  ${space};

  color: ${({ isOnSale, withCompactStyle = false }) =>
    themeGet(
      isOnSale ? (withCompactStyle ? TextColor.SaleV2 : TextColor.Sale) : TextColor.Default
    )};
`;

const Price = (props: ComponentProps<typeof UnstyledPrice>) => (
  <UnstyledPrice
    as="span"
    display="inline-block"
    alignSelf="center"
    pr={['sp_6', null, null, 'sp_8']}
    {...props}
  />
);

const UnstyledPriceLarge = styled(HeadlineH3)<PriceProps>`
  ${display};
  ${alignSelf};

  color: ${({ isOnSale, withCompactStyle = false }) =>
    themeGet(
      isOnSale ? (withCompactStyle ? TextColor.SaleV2 : TextColor.Sale) : TextColor.Default
    )};
`;

const UnstyledPriceLargeV2 = styled(HeadlineH4)<PriceProps>`
  ${display};
  ${alignSelf};

  color: ${({ isOnSale, withCompactStyle = false }) =>
    themeGet(
      isOnSale ? (withCompactStyle ? TextColor.SaleV2 : TextColor.Sale) : TextColor.Default
    )};
`;

const PriceLarge = ({
  withCompactStyle = false,
  ...props
}: ComponentProps<typeof UnstyledPriceLarge>) => {
  const Component = withCompactStyle ? UnstyledPriceLargeV2 : UnstyledPriceLarge;

  return (
    <Component
      display="inline-block"
      alignSelf="center"
      pr={['sp_6', null, null, 'sp_8']}
      ml={withCompactStyle ? 'sp_4' : 'sp_0'}
      withCompactStyle={withCompactStyle}
      {...props}
    />
  );
};

export const BasePrice = styled.span<
  DisplayProps & SpaceProps & { isOnSale?: boolean; withMarketingRed?: boolean }
>`
  ${display};
  ${space};

  color: ${({ isOnSale, withMarketingRed }) =>
    themeGet(
      isOnSale ? (withMarketingRed ? TextColor.SaleV2 : TextColor.Sale) : TextColor.Default
    )};
`;

export const priceStyles: Partial<ComponentProps<typeof BasePrice> & { isOnSale?: boolean }> = {
  display: 'inline-block',
  pr: 'sp_4',
};

const OldPrice = CaptionStrikeCS1;
const OldPriceLarger = BodyStrikeST2;

export const oldPriceStyles: ComponentProps<typeof OldPrice> = {
  as: 'span',
  pr: 'sp_4',
  color: 'sys.neutral.text.active',
  whiteSpace: 'nowrap',
};

export {
  Price,
  PriceLarge,
  PriceContainer,
  PriceContainerLarge,
  TextColor,
  OldPrice,
  OldPriceLarger,
};
